import React, { useState } from 'react';
import { View, Modal, TextInput, TouchableOpacity, StyleSheet, ScrollView, KeyboardAvoidingView, Platform, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { CustomPicker } from '../common/CustomPicker';
import { UserProfile, PersonalDetailsFormData, INDUSTRIES } from '../../types/profile';
import ThemedText from '../common/ThemedText';

const CITIES = [
  'Bangalore', 'Mumbai', 'Delhi', 'Hyderabad', 'Pune', 'Chennai', 'Kolkata',
  'Ahmedabad', 'Surat', 'Jaipur', 'Lucknow', 'Kanpur', 'Nagpur', 'Indore',
  'Thane', 'Bhopal', 'Visakhapatnam', 'Vadodara', 'Ghaziabad', 'Ludhiana',
  'Agra', 'Nashik', 'Faridabad', 'Meerut', 'Rajkot', 'Varanasi', 'Srinagar',
  'Amritsar', 'Allahabad', 'Ranchi', 'Howrah', 'Coimbatore', 'Vijayawada',
  'Jodhpur', 'Madurai', 'Raipur', 'Kota', 'Guwahati', 'Chandigarh',
];

interface EditProfileModalProps {
  visible: boolean;
  profile: UserProfile;
  onClose: () => void;
  onSave: (data: PersonalDetailsFormData) => void;
}

const EditProfileModal: React.FC<EditProfileModalProps> = ({
  visible,
  profile,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<PersonalDetailsFormData>({
    name: profile.name,
    profession: profile.profession,
    industry: profile.industry,
    city: profile.city,
    bio: profile.bio || '',
    skills: profile.skills || [],
    experienceLevel: profile.experienceLevel,
    languages: profile.languages || [],
    availability: profile.availability,
    website: profile.website || '',
  });

  const [skillsText, setSkillsText] = useState(profile.skills?.join(', ') || '');
  const [languagesText, setLanguagesText] = useState(profile.languages?.join(', ') || '');

  const handleSave = () => {
    // Validate required fields
    if (!formData.name.trim()) {
      Alert.alert('Error', 'Name is required');
      return;
    }
    if (!formData.profession.trim()) {
      Alert.alert('Error', 'Profession is required');
      return;
    }
    if (!formData.city.trim()) {
      Alert.alert('Error', 'City is required');
      return;
    }

    // Parse comma-separated values
    const skills = skillsText
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0);
    
    const languages = languagesText
      .split(',')
      .map(l => l.trim())
      .filter(l => l.length > 0);

    const dataToSave: PersonalDetailsFormData = {
      ...formData,
      skills: skills.length > 0 ? skills : undefined,
      languages: languages.length > 0 ? languages : undefined,
      bio: formData.bio?.trim() || undefined,
      website: formData.website?.trim() || undefined,
    };

    onSave(dataToSave);
    onClose();
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <KeyboardAvoidingView
        style={styles.container}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <View style={styles.header}>
          <TouchableOpacity onPress={onClose} style={styles.headerButton}>
            <Ionicons name="close" size={28} color="#000000" />
          </TouchableOpacity>
          <ThemedText style={styles.headerTitle}>Edit Profile</ThemedText>
          <TouchableOpacity onPress={handleSave} style={styles.headerButton}>
            <ThemedText style={styles.saveText}>Save</ThemedText>
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
          <View style={styles.section}>
            <ThemedText style={styles.sectionTitle}>Required Information</ThemedText>

            <View style={styles.fieldContainer}>
              <ThemedText style={styles.label}>Full Name *</ThemedText>
              <TextInput
                style={styles.input}
                value={formData.name}
                onChangeText={(text) => setFormData({ ...formData, name: text })}
                placeholder="Enter your name"
                maxLength={100}
              />
            </View>

            <View style={styles.fieldContainer}>
              <ThemedText style={styles.label}>Profession *</ThemedText>
              <TextInput
                style={styles.input}
                value={formData.profession}
                onChangeText={(text) => setFormData({ ...formData, profession: text })}
                placeholder="e.g., Software Engineer"
                maxLength={100}
              />
            </View>

            <View style={styles.fieldContainer}>
              <ThemedText style={styles.label}>Industry *</ThemedText>
              <CustomPicker
                selectedValue={formData.industry}
                onValueChange={(value) => setFormData({ ...formData, industry: value as string })}
                items={INDUSTRIES.map((industry) => ({
                  label: industry,
                  value: industry,
                }))}
                placeholder="Select industry"
              />
            </View>

            <View style={styles.fieldContainer}>
              <ThemedText style={styles.label}>City *</ThemedText>
              <CustomPicker
                selectedValue={formData.city}
                onValueChange={(value) => setFormData({ ...formData, city: value as string })}
                items={CITIES.map((city) => ({
                  label: city,
                  value: city,
                }))}
                placeholder="Select city"
              />
            </View>
          </View>

          <View style={styles.section}>
            <ThemedText style={styles.sectionTitle}>Additional Information</ThemedText>

            <View style={styles.fieldContainer}>
              <ThemedText style={styles.label}>Bio (Optional)</ThemedText>
              <TextInput
                style={[styles.input, styles.textArea]}
                value={formData.bio}
                onChangeText={(text) => setFormData({ ...formData, bio: text })}
                placeholder="Tell us about yourself (max 3 lines)"
                multiline
                numberOfLines={3}
                maxLength={300}
                textAlignVertical="top"
              />
              <ThemedText style={styles.charCount}>{formData.bio?.length || 0}/300</ThemedText>
            </View>

            <View style={styles.fieldContainer}>
              <ThemedText style={styles.label}>Skills (Optional)</ThemedText>
              <TextInput
                style={styles.input}
                value={skillsText}
                onChangeText={setSkillsText}
                placeholder="Separate with commas (e.g., React, TypeScript, UI/UX)"
                maxLength={200}
              />
              <ThemedText style={styles.hint}>Separate multiple skills with commas</ThemedText>
            </View>

            <View style={styles.fieldContainer}>
              <ThemedText style={styles.label}>Experience Level (Optional)</ThemedText>
              <CustomPicker
                selectedValue={formData.experienceLevel}
                onValueChange={(value) => setFormData({ ...formData, experienceLevel: value as string | undefined })}
                items={[
                  { label: 'Select level', value: undefined },
                  { label: 'Beginner', value: 'Beginner' },
                  { label: 'Intermediate', value: 'Intermediate' },
                  { label: 'Expert', value: 'Expert' },
                ]}
                placeholder="Select experience level"
              />
            </View>

            <View style={styles.fieldContainer}>
              <ThemedText style={styles.label}>Languages (Optional)</ThemedText>
              <TextInput
                style={styles.input}
                value={languagesText}
                onChangeText={setLanguagesText}
                placeholder="Separate with commas (e.g., English, Spanish)"
                maxLength={100}
              />
              <ThemedText style={styles.hint}>Separate multiple languages with commas</ThemedText>
            </View>

            <View style={styles.fieldContainer}>
              <ThemedText style={styles.label}>Availability (Optional)</ThemedText>
              <CustomPicker
                selectedValue={formData.availability}
                onValueChange={(value) => setFormData({ ...formData, availability: value as string | undefined })}
                items={[
                  { label: 'Select availability', value: undefined },
                  { label: 'Full-time', value: 'Full-time' },
                  { label: 'Part-time', value: 'Part-time' },
                  { label: 'Freelance', value: 'Freelance' },
                ]}
                placeholder="Select availability"
              />
            </View>

            <View style={styles.fieldContainer}>
              <ThemedText style={styles.label}>Website / Portfolio (Optional)</ThemedText>
              <TextInput
                style={styles.input}
                value={formData.website}
                onChangeText={(text) => setFormData({ ...formData, website: text })}
                placeholder="https://yourwebsite.com"
                keyboardType="url"
                autoCapitalize="none"
                maxLength={200}
              />
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F2EF',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  headerButton: {
    width: 60,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#000000',
  },
  saveText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0A66C2',
    textAlign: 'right',
  },
  content: {
    flex: 1,
  },
  section: {
    backgroundColor: '#FFFFFF',
    marginTop: 16,
    paddingHorizontal: 16,
    paddingVertical: 20,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#000000',
    marginBottom: 16,
  },
  fieldContainer: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#000000',
    marginBottom: 8,
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D0D0D0',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 15,
    color: '#000000',
  },
  textArea: {
    height: 80,
    paddingTop: 12,
  },
  pickerContainer: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D0D0D0',
    borderRadius: 6,
    overflow: 'hidden',
  },
  picker: {
    height: 50,
  },
  charCount: {
    fontSize: 12,
    color: '#999999',
    textAlign: 'right',
    marginTop: 4,
  },
  hint: {
    fontSize: 12,
    color: '#666666',
    marginTop: 4,
  },
});

export default EditProfileModal;
